import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, Settings2, Download } from "lucide-react";
import { toast } from "sonner";
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { api } from "../../lib/api";
import CrmColumn from "./CrmColumn";
import CrmPanel from "./CrmPanel";
import CrmDashboard from "./CrmDashboard";
import { NewProspect, Settings, Backup } from "./CrmModals";
import { COLUMNS, START_COLUMN, normalizeState, moveCardState, applyAuto, uid, fold } from "./crmUtils";

export default function CrmBoard({ clientId }) {
  const ro = !!clientId;
  const url = clientId ? `/admin/clients/${clientId}/crm` : "/client/crm";
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("pipeline");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [modal, setModal] = useState(null);
  const [now, setNow] = useState(Date.now());
  const timer = useRef(null);
  const dragId = useRef(null);
  const [dragging, setDragging] = useState(null);
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }));

  useEffect(() => { api.get(url).then(({ data }) => setState(normalizeState(data?.state))).catch(() => setState(normalizeState(null))); }, [url]);
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(i); }, []);

  const update = useCallback((fn, msg, uiOnly) => {
    if (ro && !uiOnly) return;
    setNow(Date.now());
    setState((s) => {
      const next = fn(s);
      if (ro) return next;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { api.put("/client/crm", { state: next }).catch(() => toast.error("Sauvegarde impossible, vérifie ta connexion")); }, 500);
      return next;
    });
    if (msg) toast.success(msg);
  }, [ro]);

  useEffect(() => {
    if (!state) return;
    const n = applyAuto(state, now);
    if (n) update(() => n, null, true);
  }, [state, now, update]);

  if (!state) return <p className="text-sm text-ink-muted">Chargement…</p>;
  const move = (id, col) => update((s) => moveCardState(s, id, col));
  const dragged = state.cards.find((c) => c.id === dragId.current);
  const q = fold(search.trim().replace(/^@/, ""));
  const match = (c) => !q || fold(c.instagram).includes(q) || fold(c.prenom).includes(q) || fold(c.nom).includes(q);
  const create = ({ instagram, link }) => {
    const at = new Date().toISOString();
    update((s) => ({ ...s, cards: [...s.cards, { id: uid(), instagram, link, prenom: "", nom: "", notes: "", column: START_COLUMN, enteredAt: at, createdAt: at, noShowCount: 0, lastRelanceAt: null, history: [{ type: "move", column: START_COLUMN, at }], client: null }] }), "Prospect ajouté");
    setModal(null);
  };
  const open = state.cards.find((c) => c.id === openId);
  const tabCls = (k) => `rounded-full px-5 py-2 text-sm transition-colors ${tab === k ? "bg-bordeaux font-medium text-white shadow-[0_0_20px_rgba(123,17,58,0.5)]" : "text-white/70 hover:text-white"}`;

  return (
    <div data-testid="crm-board" className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-full bg-[#2D181E] p-1.5">
          <button data-testid="crm-tab-pipeline" onClick={() => setTab("pipeline")} className={tabCls("pipeline")}>Pipeline</button>
          <button data-testid="crm-tab-dashboard" onClick={() => setTab("dashboard")} className={tabCls("dashboard")}>Dashboard</button>
        </div>
        {ro && <span data-testid="crm-readonly" className="ml-auto rounded-full bg-white px-3 py-1.5 text-xs font-medium text-ink-muted ring-1 ring-line">Lecture seule</span>}
        {!ro && <div className="ml-auto flex gap-2">
          <button data-testid="crm-backup-btn" onClick={() => setModal("backup")} className="flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-medium text-ink-soft hover:border-bordeaux hover:text-bordeaux"><Download size={14} /> Exporter / Importer</button>
          <button data-testid="crm-settings-btn" onClick={() => setModal("settings")} title="Réglages des délais" className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-white text-ink-soft hover:border-bordeaux hover:text-bordeaux"><Settings2 size={16} /></button>
        </div>}
      </div>
      {tab === "pipeline" && <>
        <div className="relative max-w-sm">
          <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input data-testid="crm-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un @" className="w-full rounded-full border border-line bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-bordeaux" />
        </div>
        {!ro && <p className="text-xs font-light text-ink-muted sm:hidden">Sur téléphone, appuie longuement sur une carte pour la glisser dans une autre colonne.</p>}
        <DndContext sensors={sensors} onDragStart={(e) => { dragId.current = e.active.id; setDragging(e.active.id); }} onDragCancel={() => { dragId.current = null; setDragging(null); }}
          onDragEnd={(e) => { if (e.over) move(e.active.id, e.over.id); dragId.current = null; setDragging(null); }}>
          <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
            {COLUMNS.map((c) => <CrmColumn key={c.id} col={c} state={state} q={match} ro={ro} update={update} onOpen={setOpenId} onNew={() => setModal("new")} now={now} />)}
          </div>
          <DragOverlay>{dragging && dragged ? <div className="w-[260px] rotate-2 rounded-xl border-[1.5px] border-bordeaux bg-white p-3 text-sm font-medium text-bordeaux shadow-2xl">@{dragged.instagram}</div> : null}</DragOverlay>
        </DndContext>
      </>}
      {tab === "dashboard" && <CrmDashboard state={state} />}
      {open && <CrmPanel key={open.id} card={open} ro={ro} update={update} move={move} onClose={() => setOpenId(null)}
        onDelete={() => { if (window.confirm(`Supprimer @${open.instagram} ? La carte, ses notes et son historique seront définitivement supprimés.`)) { update((s) => ({ ...s, cards: s.cards.filter((c) => c.id !== open.id) }), "Carte supprimée"); setOpenId(null); } }} />}
      {modal === "new" && <NewProspect state={state} onCreate={create} onClose={() => setModal(null)} />}
      {modal === "settings" && <Settings state={state} onClose={() => setModal(null)} onSave={(delays) => { update((s) => ({ ...s, settings: { delays } }), "Réglages enregistrés"); setModal(null); }} />}
      {modal === "backup" && <Backup state={state} onClose={() => setModal(null)} onImport={(s) => { update(() => s, "Sauvegarde importée"); setOpenId(null); setModal(null); }} />}
    </div>
  );
}
